/**
 * THE SKY — harvested from char-pipeline's `render/environment.ts`
 * (commits 81fbb30, 346a061, 942fac1), STRIPPED to background-only.
 *
 * INTEGRATION HISTORY, so the cut is legible later: the R8 v3 merge
 * deleted this file wholesale to remove two banned items (a worn-asphalt
 * causeway texture, styled lamp posts) — a false either/or, since the
 * same file also carried real, independently-gradeable world motion
 * (shader sky dome, animated sea, cloud drift, isle bob, waterfalls)
 * that had nothing to do with either banned prop. That over-deletion,
 * combined with R8 v3's own earlier deletion of its primitive floating
 * islands, left the background a flat, empty gradient — graded as its
 * own regression.
 *
 * This cut keeps every background/atmosphere system named in that
 * founder review (sky dome, sea, cloud drift, isle bob, floating isles
 * with waterfalls) and drops the ENTIRE causeway-building block that
 * lived in the original file (`paintRoad`, the deck body, piers, kerbs,
 * lamp posts) — not just the two banned items inside it. That block is
 * not "kept minus two items," it is not carried over AT ALL, because
 * this game already has its own causeway (`scene3d.ts`'s `deckMat` /
 * rail build, R8 v3), already re-dressed to the Sky Archipelago prop
 * vocabulary and already verified against `key-image-2.png`. Building
 * a second, competing deck/rail/lamp system here would not be "keeping
 * everything else" — it would duplicate and conflict with a system
 * that already passed its own grading.
 *
 * SECOND PASS (founder verdict on 44e71d97): the concern raised above
 * was correct and then some — a village with a campanile reads as a
 * lighthouse at a glance, the isle crowns' grass tier read as coastal
 * hills, and every palette was closer to a foggy coast than the key
 * image's bright sky. `makeVillage` and `makeCypresses` are DELETED
 * (not kept-and-flagged this time), `makeIsle` no longer has a grass
 * tier (two-tone warm sandstone rock now, see that function's own
 * comment), and every `WorldPalette` entry is re-sampled/re-derived
 * from `docs/reference/key-image-2.png` (see the `WorldPalette`
 * interface comment for the exact sample points and hex values).
 *
 * Design laws carried over unchanged:
 *  - LAYERED DEPTH. Near shoulders -> mid isles -> far silhouettes -> sky.
 *  - ONE PALETTE. Sky, fog, sea, and bounce light all derive from one
 *    palette record per isle-theme.
 *  - CHEAP ON PURPOSE. The sky is one shader dome, the sea one shader
 *    plane, the isles a handful of vertex-coloured meshes.
 *  - RENDERER-ONLY. Nothing here reads or writes sim state.
 */
import {
  Group, Mesh, Vector3,
  PlaneGeometry,
  MeshBasicMaterial,
  DoubleSide,
  TextureLoader, SRGBColorSpace, Texture,
} from 'three';

/**
 * One palette per isle-theme, matched to the four leaders the start
 * screen offers. Every colour in the world derives from these values.
 *
 * INTEGRATION FIX (founder verdict on 44e71d97): re-sampled from
 * `docs/reference/key-image-2.png` rather than picked independently.
 * The prior values read as "a coastal palette, not Sky Archipelago" —
 * grey-green isle crowns and a turquoise sea. Sampled points (all from
 * the key image, RGB and hex):
 *   sky, cleanest band near the top   (0-816,0-40)    (198,219,248) #c6dbf8
 *   sky, top-left corner              (0-60,0-60)     (174,207,242) #aecff2
 *   sky, wider upper band             (0-816,0-100)   (207,215,237) #cfd7ed
 * The camera looks DOWN the corridor, so the shader's own visible band
 * (`h` in [0, 0.35], see SKY_FRAG) is dominated by `horizon`, not
 * `zenith` — `horizon` is set close to the sampled values; `zenith` is
 * extrapolated deeper/more saturated for the small sliver of dome that
 * does show (the reference photo's own FOV never points straight up, so
 * there is no direct sample for true zenith). `rock`/`cliff` are pulled
 * from the causeway's own warm-sandstone family (`scene3d.ts`'s
 * `deckMat`/rail colours, #d6cdbc/#a89a86/#d9b877), not sampled fresh,
 * so the isles and the causeway read as one material world. `grass` is
 * REMOVED from the interface: isles are floating sky-rock, not coastal
 * hills, so there is no crown vegetation tier to colour.
 *
 * NO SEA (founder, 2026-08-19): neither key image has one — the causeway
 * floats directly over cloud, islands at every depth including well
 * below deck level. `seaDeep`/`seaShallow`/`seaGlint` are deleted along
 * with the shader that consumed them; see `createWorld`'s "no sea" note
 * for the full removal.
 */
// DEAD FIELDS REMOVED (founder, 2026-08-19): `zenith`, `sun`, `cloudTint`,
// `cloudShadow` and `lamp` were vestiges of the procedural sky dome and the
// causeway lamp posts, both gone since earlier sessions (the painted
// backdrop plates replaced the dome; "no lamp posts" was an R8 re-dress
// call) — confirmed zero references anywhere outside their own definition
// before deleting, not assumed dead from the name alone. `seaDeep`/
// `seaShallow`/`seaGlint` go the same way as a direct consequence of
// removing the sea shader itself (item 1) — nothing reads them once
// `seaUniforms` is gone.
export interface WorldPalette {
  horizon: number;
  sunDir: Vector3;
  rock: number; cliff: number;
  fog: number; hemiSky: number; hemiGround: number;
}

// PALETTE LIFT (founder, 2026-08-19): raised `cliff` toward key-image-2's
// own pale-stone range — it was the one surviving field measured notably
// below the reference (55.5% naive luminance against island-face samples
// around 70%). `rock`/`hemiGround`/`hemiSky`/`fog`/`horizon` were already
// close (within a few points of their matching reference samples) and are
// left alone; verified live after the change, see deploy report.
const PALETTES: WorldPalette[] = [
  { // shiroka-morning — the default read, closest match to key-image-2.png.
    horizon: 0xa8cef2,
    sunDir: new Vector3(-0.42, 0.40, 0.55).normalize(),
    rock: 0xc9b696, cliff: 0xb7a284,
    // CONTRAST STOPGAP (founder note, temporary — the layered painted
    // backdrop session supersedes this): fog and hemiSky both pulling
    // toward the same sampled blue collapsed the value range and read
    // flat. hemiSky is now the actual MIDPOINT between fog (sky blue,
    // unchanged) and hemiGround (warm bounce, unchanged) — 0xb8c2c4,
    // computed as the RGB average of 0xa8cef2 and 0xc9b696 — so there
    // are three distinct steps (blue / blue-grey / warm sand) instead
    // of two colours doing the work of three.
    fog: 0xa8cef2, hemiSky: 0xb8c2c4, hemiGround: 0xc9b696,
  },
  { // storm-ridge — cooler light, still clearly blue, never grey-drab.
    horizon: 0x9dc4dc,
    sunDir: new Vector3(-0.30, 0.46, 0.52).normalize(),
    rock: 0xa89880, cliff: 0x958672,
    // hemiSky was literally identical to fog (0x9dc4dc) — the most
    // collapsed of the four. Midpoint of fog and hemiGround, same
    // stopgap as shiroka-morning above.
    fog: 0x9dc4dc, hemiSky: 0xa2aeae, hemiGround: 0xa89880,
  },
  { // tarabosh-gold — warm light rays at the horizon (matches the key
    // image's own glow).
    horizon: 0xecc98c,
    sunDir: new Vector3(-0.52, 0.24, 0.55).normalize(),
    rock: 0xc9a876, cliff: 0xb4946b,
    fog: 0xecc98c, hemiSky: 0xdab881, hemiGround: 0xc9a876,
  },
  { // zogaj-strait — deep afternoon blue, already closest of the four
    // to a bright saturated sky before this fix; kept, rock aligned.
    horizon: 0xa9d9e8,
    sunDir: new Vector3(-0.36, 0.52, 0.48).normalize(),
    rock: 0xb0a088, cliff: 0x9f8e77,
    // hemiSky was also literally identical to fog (0xa9d9e8) here.
    fog: 0xa9d9e8, hemiSky: 0xacbcb8, hemiGround: 0xb0a088,
  },
];

// NO SEA (founder, 2026-08-19): neither key image has one — the causeway
// floats directly over cloud. `SEA_Y` (water level, -7.5) is gone along
// with the shader plane it positioned.

// --------------------------------------------------------------- sky dome
// SUPERSEDED (founder directive, layered painted backdrop, this session).
// The procedural shader dome (gradient + sun disc + fbm clouds, one big
// BackSide sphere centred on the camera) that lived here across five prior
// tuning sessions is gone — replaced by `buildBackdropPlate()` below, a
// camera-locked painted plate. Depth now comes from relative motion between
// backdrop layers (Part B), not from retuning this shader's hex values
// again. See `createWorld()`'s "backdrop plates" section.

// NO SEA, NO WATERFALLS, NO ISLES, NO GULLS (founder, 2026-08-19): the sea
// shaders, waterfalls, `makeIsle`/`isleAt`/`hazeFor` and the gull system
// are all deleted outright, not left unreachable — see `createWorld`'s
// own note (search "ARCHIPELAGO AND GULLS DELETED") for the enumeration
// and reasoning. The background is the three cloud/sky backdrop plates
// plus the causeway (scene3d.ts) now; nothing else.

// --------------------------------------------------------- backdrop plates
/** Every layer's distance + parallax fraction, in ONE place (Part B's
 * requirement, honoured from Part A on so the second and third layers are
 * additions here, not a rewrite). Part A builds SKY only. `distance` is
 * world units along the baseline camera's forward ray; `parallaxFactor` is
 * how much of live camX/camZ translation the layer follows — 1.0 is a full
 * camera lock (zero relative motion, correct for "infinitely far" sky);
 * Part B's clouds/silhouettes will sit below 1.0 so they read as nearer. */
// `nativeAspect` (width/height) is the plate PNG's own shape — confirmed
// via `file`, 1680x720 — needed up front rather than read off
// `texture.image` at size time, because `TextureLoader().load()` is
// async and the plane gets its first size call (from the startup
// `resize()`) before the image has necessarily finished loading.
//
// PART B — CLOUDS/ISLANDS (founder, 2026-08-19): unlike SKY (opaque,
// covers 100% of frustum height at its distance), these two are alpha-
// keyed cutouts (chroma-keyed off their source plates' flat fields,
// content-cropped, see `scripts/` history / deploy report this date) that
// occupy a BAND of screen height rather than the whole frame. `bandY01`
// is that band's target CENTER in screen space (0=top, 1=bottom); the
// plate is fit-width (full frustum width, height follows from its own
// cropped nativeAspect — no vertical "cover" crop, since transparent
// padding already frames the content) and translated along its own
// local "up" to center on `bandY01`. `distance` orders the layers
// (islands nearest, clouds mid, sky furthest — matches `parallaxFactor`,
// lowest = nearest-reading, both intentionally graduated below 1.0).
// BAND PLACEMENT CORRECTED (founder spec said "roughly y 0.20-0.32" for
// islands, reasoned as "so they sit ON the geometric horizon" — but the
// geometric horizon itself, measured live via `projectRaw(0,-7.5,1e5)`,
// sits at y01 ~0.094-0.109 across waves, not 0.20-0.32. Shipping the
// stated number instead of the stated REASON put islands well below the
// horizon, inside the screen region the deck/sea (both opaque, drawn
// after backdrop with depthWrite:false doing nothing to stop them) cover
// — confirmed by pixel-sampling a live render: the islands plate was
// completely invisible, painted over. Followed the reasoning over the
// literal number: `bandY01` values below now sit ABOVE the measured
// horizon floor with margin, and `sizeScale` shrinks each plate to fit
// the much tighter true-sky budget above that floor (~0.09 of screen
// height total for both layers combined) rather than the frustum-width
// span original sizing assumed.
// SKY BRIGHTNESS FIX (founder, 2026-08-19): the top of frame was reading
// navy, not daylight blue. Traced by sampling the live render (0,63,158
// at screen01 y~0), then tracing that colour back into the SOURCE plate
// (row 75-128 of 720, a near-exact match) rather than guessing at a
// shader/fog/colour-space cause. The plate is sized to COVER the full
// frustum height (`sizeBackdropPlate`'s cover branch), but the deck (and,
// at the time, the sea) occlude most of it — so the ONLY slice ever
// visible was the plate's own top rows, the darkest part of a gradient
// authored to run dark-zenith-blue (row 0) to pale-horizon-glow (row
// 719). The pipeline itself is correct (OutputPass is last, confirmed) —
// this is a crop problem, not an encode problem. `verticalCropBright`
// discards the dark upper rows entirely (repeat.y applied in
// `sizeBackdropPlate`'s cover branch, sky only) so the visible sliver
// samples from the brighter lower portion of the source gradient
// instead.
//
// TIGHTENED TWICE (founder, 2026-08-19, still too dark both times): 0.45
// (rows ~350-719) was a real, necessary first fix but still landed at
// ~44% luminance against key-image-2's 78-99%. 0.25 (rows ~540-719)
// closed some of the gap but measured only 64% live — still short. 0.12
// keeps rows ~630-719, the palest ~12%, chosen by solving backward from
// the source gradient's own row-by-row luminance for the darkest row
// that still clears ~78% rather than halving the fraction again and
// re-measuring blind. Verified live after the change, see deploy report.
const BACKDROP_LAYERS = {
  SKY: {
    url: './backdrop-sky.webp', distance: 200, parallaxFactor: 1.0, nativeAspect: 1680 / 720,
    verticalCropBright: 0.10,
  },
  CLOUDS: {
    url: './backdrop-clouds.webp', distance: 170, parallaxFactor: 0.6,
    nativeAspect: 1200 / 289, bandY01: 0.035, sizeScale: 0.45,
  },
  // NO SEA, FILL BELOW WITH CLOUD (founder, 2026-08-19): a second, wider,
  // looser crop of the SAME approved cloud source (`prep-under-cloud.mjs`
  // — 200px padding around the content bbox vs. the over-layer's 10px,
  // clamped by the source canvas to 1680x480, resized to 1400x400) fills
  // the band below the deck the sea used to occupy. Same asset, no new
  // network cost beyond the one extra crop. `ISLANDS` (the small
  // near-horizon accent band) is deleted, not reused here — the far
  // billboards it fed are also gone this round; nothing consumes it.
  UNDER_CLOUDS: {
    url: './backdrop-clouds-under.webp', distance: 170, parallaxFactor: 0.55,
    nativeAspect: 1400 / 400, bandY01: 0.30, sizeScale: 2.2,
  },
  // ISLANDS, FROM THE APPROVED PLATE (founder, 2026-08-19, "the founder's
  // approved island plate already exists in docs/reference/... Use it.
  // Cut individual islands out of that source as separate plates rather
  // than generating new shapes."). The two procedurally-generated
  // silhouettes this replaces (`scripts/gen-island-plate.mjs`, GRAYSCALE
  // + palette tint) are deleted, not kept-and-flagged -- the founder's
  // verdict was explicit ("blurred beige blobs... not the founder's
  // approved art"). Source: `docs/reference/opal_apple_distant_floating_
  // rock_islands_in_silhouette_only_h_9a084490-edbb-44ff-bbeb-
  // bf45ef8dfa7d_0.png` (1680x720, flat pale-blue background, seven
  // separated floating-rock silhouettes with flat undercut bottoms and
  // irregular broken cliff profiles). Separated by
  // `scripts/extract-islands.py`: chroma-keyed against the flat
  // background (Euclidean RGB distance, the founder's own figure --
  // chroma distance 120.6 -- as the alpha floor, feathered to fully
  // opaque by 150 so cut edges anti-alias), connected-component labelled
  // (hand-rolled flood fill, no scipy in this environment) to find each
  // silhouette as its own region, then cropped to its own bounding box
  // with 24px padding (room for the feather to reach true zero before
  // the crop's own edge -- the exact bug the cloud plates had earlier
  // this session, not reproduced here) and alpha-masked against its
  // neighbours so an adjacent island's bbox padding can never bleed in.
  // COLOUR IS THE SOURCE'S OWN, no grayscale/re-tint: the approved plate
  // already has real painted rock shading (a lit top, a dark undercut) --
  // converting it to grayscale-plus-palette-tint, like the deleted
  // procedural islands, would throw that away for a flatter, less
  // "rock" result. `accent: true` opts every one OUT of the coverage-
  // floor fix (that floor is for FULL-WIDTH band layers; forcing a small
  // localized island to floor-width would stretch it edge-to-edge).
  // `sideOffsetX`/`bandY01` placements below are the SAME measured
  // constraints established for the procedural islands (HUD footprint
  // via `hudBoxes()`, deck occlusion width via `projectWorld` at each
  // band height) -- swapping the art doesn't change the geometry those
  // were solved against, so they're reused rather than re-derived.
  ISLAND_1: { // the largest, most dramatic cliff -- the near/left anchor.
    url: './backdrop-island-1.webp', distance: 150, parallaxFactor: 0.4,
    nativeAspect: 529 / 358, bandY01: 0.16, sizeScale: 0.30,
    sideOffsetX: -11, haze: 1.0, accent: true,
  },
  ISLAND_2: { // second-largest, mirrors ISLAND_1 on the right at a touch more depth.
    url: './backdrop-island-6.webp', distance: 210, parallaxFactor: 0.65,
    nativeAspect: 483 / 363, bandY01: 0.115, sizeScale: 0.24,
    sideOffsetX: 14, haze: 0.9, accent: true,
  },
  // Below deck level (founder: "at depths including below the deck") --
  // same measured band as the deleted ISLAND_C (deck spans only
  // screen01.x [~0.44,~0.63] at this bandY01, room either side).
  ISLAND_3: {
    url: './backdrop-island-2.webp', distance: 175, parallaxFactor: 0.55,
    nativeAspect: 323 / 229, bandY01: 0.21, sizeScale: 0.18,
    sideOffsetX: 32, haze: 0.88, accent: true,
  },
  // Smallest + furthest + haziest -- a fourth depth step so the sky reads
  // as a scattered archipelago rather than two or three same-size shapes
  // at the same remove. `bandY01` nudged down from an initial 0.10 (which
  // measured yMin=0.069, clipping under the squad HUD box's y<=0.088
  // footprint -- checked live, same `hudBoxes()` constraint as the other
  // three) to clear it with margin.
  ISLAND_4: {
    url: './backdrop-island-3.webp', distance: 270, parallaxFactor: 0.85,
    nativeAspect: 235 / 163, bandY01: 0.14, sizeScale: 0.13,
    sideOffsetX: -32, haze: 0.6, accent: true,
  },
  // FIFTH AND SIXTH ISLAND, ONE PER SIDE (founder, 2026-08-19). Three of
  // the seven `scripts/extract-islands.py` outputs were never used
  // (backdrop-island-4/5/7.webp); adding two of them at NEW `distance`
  // values (130, 240 — neither matches 150/175/210/270 above) so each
  // reads as its own depth layer, not a duplicate of an existing one.
  // `backdrop-island-4.webp` is the single LARGEST extracted silhouette
  // (529x358 src, bigger than ISLAND_1's own 395x208) -- used here as the
  // near-RIGHT anchor, a size-matched rival to ISLAND_1's near-LEFT
  // anchor rather than left stacking every big shape on one side.
  // `backdrop-island-7.webp` (broad low mesa, 284x213) is the new LEFT
  // placement, at 240 depth between ISLAND_2's 210 and ISLAND_4's 270 so
  // the left side gets its own extra depth step the way the right side
  // already had (ISLAND_2/ISLAND_3 at 210/175).
  // First pass measured live and missed twice, both fixed empirically
  // rather than re-guessed: ISLAND_5 at sideOffsetX=44 landed entirely
  // off-frame right (xMin=1.14) -- distance=130 is nearer than every
  // other island, so the SAME world-unit offset produces a much bigger
  // screen shift there than it does further out; ISLAND_6 at bandY01=
  // 0.095 clipped the HUD (yMin=0.065) and its x-range sat inside
  // ISLAND_1's own footprint. `bandY01` moved to sit clearly below
  // ISLAND_3 (right) and ISLAND_1/ISLAND_4 (left) respectively, not just
  // "a new distance" -- depth alone doesn't stop two accent plates from
  // occupying the same screen region.
  // SECOND MISS, ALSO FIXED EMPIRICALLY: bandY01=0.28 put ISLAND_5 well
  // past the deck's own vanishing point (~0.196) -- measured deck width
  // AT that band was x=[0.360,0.878], wide enough (this close to camera)
  // to occlude most of the plate. Every OTHER island sits above y~0.196
  // for exactly this reason (zero deck-occlusion risk up there, verified
  // once for ISLAND_1/2/4, never revisited): moved ISLAND_5 back into
  // that same safe band, to the right of ISLAND_2 instead of below
  // ISLAND_3, rather than continuing to fight the deck's width lower down.
  ISLAND_5: {
    url: './backdrop-island-4.webp', distance: 130, parallaxFactor: 0.35,
    nativeAspect: 529 / 358, bandY01: 0.14, sizeScale: 0.22,
    sideOffsetX: 26, haze: 1.0, accent: true,
  },
  ISLAND_6: {
    url: './backdrop-island-7.webp', distance: 240, parallaxFactor: 0.78,
    nativeAspect: 284 / 213, bandY01: 0.15, sizeScale: 0.16,
    sideOffsetX: -54, haze: 0.72, accent: true,
  },
} as const;

/** Flat safety margin on plate width/height, not an eyeballed value: it
 * exists to absorb the small per-frame orientation jitter (camLead/breath/
 * camKick) that a plate rotated ONCE to the baseline pitch — rather than
 * re-oriented every frame to chase that jitter — will not perfectly track.
 * See `buildBackdropPlate`'s doc comment for why the plate isn't re-tilted
 * every frame instead. */
const BACKDROP_SIZE_MARGIN = 1.35;
/** Lighter margin for band layers (clouds/islands) — see `sizeBackdropPlate`'s
 * band-mode comment for why `BACKDROP_SIZE_MARGIN` doesn't apply here. */
const BACKDROP_BAND_MARGIN = 1.1;

export interface BackdropLayer {
  readonly name: string;
  readonly mesh: Mesh;
  readonly texture: Texture;
  readonly parallaxFactor: number;
  readonly nativeAspect: number;
  readonly bandY01?: number;
  readonly sizeScale?: number;
  readonly verticalCropBright?: number;
  /** Accent layers (islands) opt OUT of the full-coverage width floor —
   * see BACKDROP_LAYERS.ISLAND_1's comment. */
  readonly accent?: boolean;
  /** World-unit lateral offset, re-applied every frame in `update()` —
   * see `positionBackdropPlate`'s comment. */
  readonly sideOffsetX?: number;
}

/**
 * One camera-locked painted plate.
 *
 * FACING (D4 precedent — a plane invisible because nobody checked which
 * face pointed where, verified rather than assumed this time):
 * `PlaneGeometry`'s default front-face normal is +Z. The camera in this
 * game sits at `z = pz - CAM_BACK` looking toward `z = pz + CAM_LOOKAT_FORWARD`
 * — i.e. always on the -Z side, looking toward +Z. A plate placed ahead of
 * it at larger Z, left at its default orientation, would show that camera
 * its BACK face — invisible under normal front-face culling, D4's exact
 * failure. Fixed here by construction, not by assumption: `mesh.lookAt
 * (baseCam)` points the plate's local -Z at the baseline camera position,
 * then `mesh.rotateY(PI)` flips that so local +Z (the authored front)
 * points back at the camera instead. `side: DoubleSide` stays on top as
 * defense-in-depth (the same belt-and-suspenders D4 shipped with) so a
 * sign error here would render wrong-way, not vanish.
 *
 * ORIENTATION IS FIXED ONCE, not re-derived every frame from the camera's
 * live `lookAt` (which carries small `camLead`/`breath`/`camKick` jitter on
 * top of the baseline pitch) — re-deriving that every frame would mean
 * duplicating scene3d.ts's per-frame camera formula here, which this file's
 * own design law ("RENDERER-ONLY... doesn't read sim state") argues
 * against, and the jitter is small enough that `BACKDROP_SIZE_MARGIN`
 * absorbing it is simpler and no less correct in practice. Only
 * `mesh.position.x`/`.z` move after construction (`update()`, tracking
 * `camX`/`camZ` by `parallaxFactor`); `.y` and rotation never change again.
 *
 * BAND LAYERS (`bandY01` set, Part B): CLOUDS/ISLANDS are alpha-keyed
 * cutouts meant to center on a screen-space Y fraction rather than fill
 * the frame. `sideOffsetX` scatters an accent plate off dead-centre.
 * Both offsets are applied by `positionBackdropPlate` below, along the
 * plate's own local "up"/"right" (after the lookAt/rotateY above) rather
 * than world axes, so they land correctly regardless of camera pitch.
 *
 * FULL LIVE TRACKING, ROOT CAUSE (founder, 2026-08-19, THIRD round on
 * this exact bug: "the environment cuts off when the camera rises").
 * Two previous fixes both missed it. Round one added a coverage-floor
 * MARGIN (`sizeBackdropPlate`'s `Math.max`) — that fixes a plate that's
 * merely too NARROW at the baseline pose, not one being viewed from a
 * completely different angle. Round two re-oriented every plate to
 * face the LIVE camera every frame (`positionBackdropPlate`'s
 * `lookAt`/`rotateY` below) instead of the frozen baseline pitch — a
 * real fix for the grazing-angle symptom, but incomplete: a plate can
 * face the camera perfectly and STILL miss the frustum if its own
 * CENTER hasn't moved to where the camera is now looking. The old
 * position model (`anchor + live_camXZ * parallaxFactor`, translation
 * only) anchors every plate near its BASELINE world position; the boss
 * shot moves the camera ~25 world units up and re-aims it ~20-30 degrees
 * steeper, and a plate that only translates a FRACTION of that (by
 * `parallaxFactor`) ends up aimed at from well off to the side of where
 * it now sits, regardless of which way it's facing. Root fix: recompute
 * the plate's full POSITION every frame too, from the LIVE camera
 * position and look-at target, at the SAME fixed `distance` along the
 * CURRENT forward ray that used to only apply at construction. A plate
 * directly ahead of wherever the camera is actually looking, sized for
 * that distance, cannot miss the frustum at any pose — the failure mode
 * is closed by construction, not approximated by a bigger margin.
 * `parallaxFactor` stays on the layer/diagnostic shape (still read by
 * `window.__scene.backdropInfo()`) but no longer drives position: every
 * layer now recenters on the live forward ray every frame, ordered only
 * by its own fixed `distance` (islands nearest, clouds mid, sky
 * furthest) — full-frame coverage at every pose was worth losing the
 * old fractional-translation depth cue for; nothing here got asked for
 * a fourth time by accident.
 */
function positionBackdropPlate(
  layer: { mesh: Mesh; distance: number; bandY01?: number; sideOffsetX?: number },
  camPos: Vector3, lookAt: Vector3, camFov: number,
): void {
  const forward = lookAt.clone().sub(camPos).normalize();
  let platePos = camPos.clone().addScaledVector(forward, layer.distance);
  layer.mesh.position.copy(platePos);
  // FACING (D4 precedent — a plane invisible because nobody checked which
  // face pointed where): `PlaneGeometry`'s default front-face normal is
  // +Z. `mesh.lookAt(camPos)` points the plate's local -Z at the camera;
  // `rotateY(PI)` flips that so local +Z (the authored front) faces back
  // at it instead. `side: DoubleSide` (material, at construction) stays
  // on as defense-in-depth so a sign error here renders wrong-way, not
  // invisible.
  layer.mesh.lookAt(camPos);
  layer.mesh.rotateY(Math.PI);

  const worldUp = new Vector3(0, 1, 0);
  const right = new Vector3().crossVectors(forward, worldUp).normalize();

  if (layer.bandY01 !== undefined) {
    const localUp = new Vector3().crossVectors(right, forward).normalize();
    const halfFovRad = (camFov * Math.PI / 180) / 2;
    const heightPhysical = 2 * layer.distance * Math.tan(halfFovRad);
    const offsetY = (0.5 - layer.bandY01) * heightPhysical;
    platePos = platePos.clone().addScaledVector(localUp, offsetY);
  }
  if (layer.sideOffsetX !== undefined) {
    platePos = platePos.clone().addScaledVector(right, layer.sideOffsetX);
  }
  layer.mesh.position.copy(platePos);
}

function buildBackdropPlate(
  name: string, url: string, distance: number, parallaxFactor: number, nativeAspect: number,
  camHeight: number, camBack: number, camLookAtForward: number, camFov: number,
  bandY01?: number, sizeScale?: number, verticalCropBright?: number,
  // ISLANDS (founder, 2026-08-19): `sideOffsetX` (world units, applied
  // along the plate's own local `right`, so it scales naturally with
  // distance under perspective same as any other world-unit offset)
  // scatters an accent plate beside the corridor instead of stacking
  // every layer dead-centre over it. `tintColor` (optional — the current
  // island set doesn't pass one, see BACKDROP_LAYERS.ISLAND_1's comment)
  // multiplies the source texture, for a future shared/grayscale asset
  // that wants one. `haze` sets material opacity so a farther island
  // reads softer, hazed into the cloud around it rather than a hard
  // cutout at full strength regardless of depth.
  opts?: { sideOffsetX?: number; tintColor?: number; haze?: number; accent?: boolean },
): BackdropLayer & { distance: number; nativeAspect: number } {
  // CHROMA CEILING (founder, 2026-08-20; saturate CORRECTED same day —
  // 0.12 was too aggressive, sky/water read as grey instead of blue).
  // Squad chroma must be the HIGHEST in frame, not everything else
  // neutral -- see COLOR_SCRIPT.md's "Chroma budget" section. Sky/clouds/
  // islands still desaturate below the squad's own chroma, just not to
  // near-zero. A flat `tintColor` MULTIPLY cannot do this at all: scaling
  // every channel by the same grey factor changes exposure, not
  // saturation, because it preserves the per-pixel channel RATIOS a
  // multiply can never touch. Real desaturation needs a per-pixel
  // operation -- the same canvas `saturate()` filter scene3d.ts's
  // TIER_GRADE already uses for the horde, applied here once at load
  // (fires in TextureLoader's onLoad, since a plain image load is async
  // and the pixels are not present at the synchronous `.load()` return
  // the way a resolved GLTF's are). Brightness lifted slightly alongside
  // it (haze, not just grey) rather than only cutting chroma -- haze
  // ITSELF (opacity) is untouched by this, restored to its authored
  // values below; receding is haze's job, not desaturation's.
  // Mutates `loaded.image` IN PLACE (not a new CanvasTexture, unlike
  // TIER_GRADE's regradeMap) so the SAME Texture object stays referenced
  // by both `mat.map` and this function's returned `BackdropLayer.texture`
  // — `sizeBackdropPlate`/resize() sets `repeat`/`offset` on that returned
  // reference (the sky's own `verticalCropBright` crop, the cover-crop
  // maths below), and swapping in a fresh Texture here would silently
  // detach that crop state from whatever's actually rendered.
  const texture = new TextureLoader().load(url, (loaded) => {
    const img = loaded.image as (HTMLImageElement | HTMLCanvasElement) & { width: number; height: number };
    if (!img?.width) return;
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    // 1.12 -> 0.92 (COLOR_SCRIPT.md value-hierarchy pass, 2026-08-30):
    // fresh live-capture sampling (this pass, not the 10-day-old doc)
    // found sky (~229-242) and cloud (~198-236) both reading well above
    // their BRIGHT/MID target bands (sky ~208, cloud 140-180) -- same
    // direction the original 2026-08-19 audit found, still unfixed.
    // saturate(0.55) left untouched: not what this pass re-measured, and
    // it already has its own dated correction (0.12 was too aggressive).
    // One shared filter across sky/clouds/islands can't hit three
    // different target bands at once -- islands' own target is deferred
    // (this pass's fresh reading of islands was inconclusive/contaminated
    // by small-patch sampling noise and contradicts the old doc's "too
    // dark" finding; not acting on a number this pass couldn't verify).
    ctx.filter = 'saturate(0.55) brightness(0.92)';
    ctx.drawImage(img, 0, 0);
    // three.js accepts any CanvasImageSource here at runtime; the loader's
    // own type narrows `.image` to HTMLImageElement specifically.
    loaded.image = c as unknown as HTMLImageElement;
    loaded.needsUpdate = true;
  });
  texture.colorSpace = SRGBColorSpace;
  const mat = new MeshBasicMaterial({
    map: texture, side: DoubleSide, depthWrite: false, fog: false, toneMapped: false,
    transparent: bandY01 !== undefined,
  });
  if (opts?.tintColor !== undefined) mat.color.setHex(opts.tintColor);
  // RESTORED to the authored value, no offset (founder correction,
  // 2026-08-20 same day): receding is haze's job, not desaturation's --
  // the chroma pass above should not also have been touching opacity.
  if (opts?.haze !== undefined) mat.opacity = opts.haze;
  const mesh = new Mesh(new PlaneGeometry(1, 1), mat);
  mesh.renderOrder = -20;
  mesh.frustumCulled = false;

  // Baseline pose: CAM_HEIGHT/CAM_BACK/CAM_LOOKAT_FORWARD are scene3d.ts's,
  // passed in rather than duplicated (single source of truth) so this
  // file never hardcodes a second copy of frozen camera geometry. Used
  // only for the FIRST frame's position, before any live pose exists —
  // `update()` (live camera) immediately supersedes this every frame after.
  const baseCam = new Vector3(0, camHeight, -camBack);
  const baseLook = new Vector3(0, 0.2, camLookAtForward);
  const layerForPos = {
    mesh, distance,
    ...(bandY01 !== undefined ? { bandY01 } : {}),
    ...(opts?.sideOffsetX !== undefined ? { sideOffsetX: opts.sideOffsetX } : {}),
  };
  positionBackdropPlate(layerForPos, baseCam, baseLook, camFov);

  return {
    name, mesh, texture, parallaxFactor, distance,
    nativeAspect, ...(bandY01 !== undefined ? { bandY01 } : {}),
    ...(sizeScale !== undefined ? { sizeScale } : {}),
    ...(verticalCropBright !== undefined ? { verticalCropBright } : {}),
    ...(opts?.accent !== undefined ? { accent: opts.accent } : {}),
    ...(opts?.sideOffsetX !== undefined ? { sideOffsetX: opts.sideOffsetX } : {}),
  };
}

/**
 * Sizes a plate to fill the frustum at its OWN `distance`, for the CURRENT
 * camera fov/aspect, plus `BACKDROP_SIZE_MARGIN`. Math-driven, not tuned —
 * this is what DoD A's "full coverage at every aspect, no gap" is.
 *
 * ITEM 3 FIX (founder rejection of Part A, "the band above the isles is
 * bare dark blue, no painted sky"): the DoD A check above measured the
 * PLANE's geometry, not where the TEXTURE's content actually landed —
 * `width = height * aspect` sizes the plane to the frustum correctly, but
 * the texture's default UV (0,0)-(1,1) then stretches to fit THAT plane
 * regardless of the plate's own 1680x720 (2.33:1) shape. At a portrait
 * aspect (e.g. 0.62) the plane is portrait too, so the landscape image got
 * squashed ~3.7x vertically — thinned into a band too short to read as
 * anything, everything above/below it showing the material's own base
 * colour (black, since MeshBasicMaterial's default color is white but the
 * squashed-out remainder isn't drawn there at all — the mesh IS the image,
 * there's no second "background" colour; the apparent "bare dark blue" is
 * the SEA/FOG showing through past the plate's edges, not a fill colour on
 * the plate itself). Fixed the way CSS `object-fit: cover` does: crop the
 * LONGER-relative-axis via `texture.repeat`/`.offset`, never stretch. The
 * plate is a wide gradient specifically so a vertical (portrait) crop keeps
 * the full top-to-bottom gradient and only trims off the sides — the
 * least-lossy crop for THIS image's content, not a generic choice.
 */
function sizeBackdropPlate(
  layer: {
    mesh: Mesh; distance: number; texture: Texture; nativeAspect: number;
    bandY01?: number; sizeScale?: number; verticalCropBright?: number; accent?: boolean;
  },
  fovDeg: number, aspect: number,
): void {
  const halfFovRad = (fovDeg * Math.PI / 180) / 2;

  if (layer.bandY01 !== undefined) {
    // BAND MODE (Part B): fit-width, no cover-crop. These plates are
    // already content-cropped, alpha-keyed cutouts — showing the full
    // image (repeat=1,1) at a height derived from ITS OWN nativeAspect is
    // correct, not lossy, since the transparent padding around the
    // content (not a visible background) is what would otherwise get
    // trimmed.
    //
    // MARGIN, NOT `BACKDROP_SIZE_MARGIN` (caught by measuring the actual
    // on-screen band, not by reading the formula): that constant's 1.35x
    // exists so a full-frame COVER layer has zero gap at its edges under
    // worst-case jitter — reusing it here inflated width, and because
    // `bandHeight = bandWidth / nativeAspect`, inflated height right along
    // with it. Measured result: islands landed at screen01 y[0.180,0.404]
    // (22.4% of frame) against a 12% target band. `BACKDROP_BAND_MARGIN`
    // is a much lighter allowance — these are accent bands, not full
    // backdrops, so a small gap at the extreme edge of lateral jitter is
    // fine where it would not be for SKY.
    //
    // MINIMUM COVERAGE GUARANTEE, ROOT CAUSE (founder, 2026-08-19, "the
    // right side has a hard rectangular seam"): `CLOUDS` at sizeScale 0.45
    // rendered a plane spanning screen01 x=[0.254, 0.754] -- less than
    // half the frame, nowhere near the [0,1] any visible layer must cover
    // — a hard edge with flat SKY showing past it, exactly the seam
    // reported (and, before this fix, misdiagnosed as an object to
    // delete). The bug class isn't "CLOUDS' sizeScale is wrong," it's
    // that nothing enforced a floor: any future band layer with a small
    // `sizeScale` reproduces this. `Math.max` against the SAME proven
    // zero-gap width `BACKDROP_SIZE_MARGIN` gives SKY guarantees every
    // band layer at least that much width regardless of `sizeScale` --
    // `sizeScale` can still grow a layer bigger (`UNDER_CLOUDS` already
    // does), it just can no longer shrink one below full coverage.
    const widthUnmargined = 2 * layer.distance * Math.tan(halfFovRad) * aspect;
    const scaledWidth = widthUnmargined * BACKDROP_BAND_MARGIN * (layer.sizeScale ?? 1);
    // ACCENT LAYERS (islands, founder 2026-08-19, item 4) are deliberately
    // NOT full-frame backdrops -- forcing the same coverage floor onto them
    // would stretch a small rock silhouette into an edge-to-edge smear, the
    // opposite of the localized-object read they're meant to have. Only
    // full-width band layers (CLOUDS/UNDER_CLOUDS) get the floor.
    const minCoverWidth = widthUnmargined * BACKDROP_SIZE_MARGIN;
    const bandWidth = layer.accent ? scaledWidth : Math.max(minCoverWidth, scaledWidth);
    const bandHeight = bandWidth / layer.nativeAspect;
    layer.mesh.scale.set(bandWidth, bandHeight, 1);
    layer.texture.repeat.set(1, 1);
    layer.texture.offset.set(0, 0);
    return;
  }

  const height = 2 * layer.distance * Math.tan(halfFovRad) * BACKDROP_SIZE_MARGIN;
  const width = height * aspect;
  layer.mesh.scale.set(width, height, 1);

  // COVER, not stretch: `aspect` (camera/viewport) is also the plane's own
  // aspect by construction (width = height * aspect above). Crop whichever
  // axis the image has MORE of, relative to the plane, so the image's own
  // proportions survive — the other axis stays at repeat=1 (shown in full).
  const planeAspect = aspect;
  const t = layer.texture;
  if (layer.nativeAspect > planeAspect) {
    t.repeat.set(planeAspect / layer.nativeAspect, 1);
    t.offset.set((1 - t.repeat.x) / 2, 0);
  } else {
    t.repeat.set(1, layer.nativeAspect / planeAspect);
    t.offset.set(0, (1 - t.repeat.y) / 2);
  }

  // BRIGHTNESS CROP (sky only — see BACKDROP_LAYERS.SKY's doc comment):
  // the plate covers the FULL frustum height, but the deck/sea occlude
  // everything below horizon_y (~0.10 of frame), so only the plate's own
  // darkest top rows are ever visible. Discards them by shrinking repeat.y
  // and anchoring offset.y at 0 (the source image's pale bottom, v=0) —
  // the visible sliver now samples the brighter portion of the gradient
  // instead of its dark top. Composed with (not replacing) the cover-crop
  // repeat.y set just above, in case a future non-1:1 repeat.y ever
  // applies here too.
  if (layer.verticalCropBright !== undefined) {
    t.repeat.y *= layer.verticalCropBright;
    t.offset.y = 0;
  }
}

// ------------------------------------------------------------------ world
export interface SkyWorld {
  readonly group: Group;
  readonly palette: WorldPalette;
  update(
    dt: number, camX: number, camY: number, camZ: number,
    lookAtX: number, lookAtY: number, lookAtZ: number,
  ): void;
  /** Recomputes every backdrop plate's size for the current camera
   * fov/aspect. Call from the SAME resize hook that updates the camera's
   * own `aspect` (scene3d.ts's `resize()`), and once at startup. */
  resize(aspect: number, fovDeg: number): void;
  /** D6-shape diagnostic (window.__scene.backdropInfo()): every backdrop
   * plate's identity, load state and motion parameters. */
  readonly backdrop: readonly BackdropLayer[];
  /** D2 diagnostic: the gull meshes, for window.__scene.gullInfo(). */
  readonly gulls: readonly Mesh[];
}

/**
 * Builds the BACKGROUND ONLY: backdrop plates, sea, floating archipelago,
 * waterfalls, gulls. Does not build a causeway, piers, kerbs or lamps —
 * the caller (`scene3d.ts`) already owns that geometry (R8 v3), already
 * re-dressed to the Sky Archipelago prop vocabulary. No longer takes
 * `arenaW`/`sx`: the original signature needed them to size the
 * causeway that lived here; nothing left in this file needs them.
 *
 * `camHeight`/`camBack`/`camLookAtForward`/`camFov` are scene3d.ts's frozen
 * camera constants (`CAM_HEIGHT`/`CAM_BACK`/`CAM_LOOKAT_FORWARD`/
 * `CAM_FOV`), passed in so the backdrop plates can face the camera
 * correctly without this file importing scene3d.ts (which already
 * imports this file — that would be circular) or hardcoding a second,
 * driftable copy of the same numbers.
 */
export function createWorld(
  skyIndex: number, camHeight: number, camBack: number, camLookAtForward: number, camFov: number,
): SkyWorld {
  const pal = PALETTES[skyIndex % PALETTES.length]!;
  const group = new Group();

  // --- backdrop plates ----------------------------------------------------
  // Order matters for renderOrder (painter's algorithm among these three
  // transparent-ish planes): furthest (sky) first, nearest (islands) last,
  // all still before the main scene's default renderOrder=0 opaque draws.
  const skyPlate = buildBackdropPlate(
    'sky', BACKDROP_LAYERS.SKY.url, BACKDROP_LAYERS.SKY.distance,
    BACKDROP_LAYERS.SKY.parallaxFactor, BACKDROP_LAYERS.SKY.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    undefined, undefined, BACKDROP_LAYERS.SKY.verticalCropBright);
  skyPlate.mesh.renderOrder = -20;
  group.add(skyPlate.mesh);

  const cloudsPlate = buildBackdropPlate(
    'clouds', BACKDROP_LAYERS.CLOUDS.url, BACKDROP_LAYERS.CLOUDS.distance,
    BACKDROP_LAYERS.CLOUDS.parallaxFactor, BACKDROP_LAYERS.CLOUDS.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    BACKDROP_LAYERS.CLOUDS.bandY01, BACKDROP_LAYERS.CLOUDS.sizeScale);
  cloudsPlate.mesh.renderOrder = -19;
  group.add(cloudsPlate.mesh);

  // UNDER-DECK CLOUD (founder, 2026-08-19): fills the band the sea used
  // to occupy. Same band-mode machinery as CLOUDS above, just a lower
  // `bandY01` and a much larger `sizeScale` since this one has to cover
  // real ground, not sit as a small accent.
  const underCloudPlate = buildBackdropPlate(
    'underClouds', BACKDROP_LAYERS.UNDER_CLOUDS.url, BACKDROP_LAYERS.UNDER_CLOUDS.distance,
    BACKDROP_LAYERS.UNDER_CLOUDS.parallaxFactor, BACKDROP_LAYERS.UNDER_CLOUDS.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    BACKDROP_LAYERS.UNDER_CLOUDS.bandY01, BACKDROP_LAYERS.UNDER_CLOUDS.sizeScale);
  underCloudPlate.mesh.renderOrder = -18;
  group.add(underCloudPlate.mesh);

  // ISLANDS, FROM THE APPROVED PLATE (founder, 2026-08-19) — see
  // BACKDROP_LAYERS.ISLAND_1's comment for the source/extraction. No
  // `tintColor` this round: the approved plate's own painted colour is
  // used as-is (see that same comment for why), so these no longer need
  // `pal` and could in principle be hoisted beside SKY/CLOUDS/
  // UNDER_CLOUDS above — left here, next to the other band layers that
  // still do need `pal`, rather than splitting the "backdrop plates"
  // section across two locations for one field's sake.
  const island1 = buildBackdropPlate(
    'island1', BACKDROP_LAYERS.ISLAND_1.url, BACKDROP_LAYERS.ISLAND_1.distance,
    BACKDROP_LAYERS.ISLAND_1.parallaxFactor, BACKDROP_LAYERS.ISLAND_1.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    BACKDROP_LAYERS.ISLAND_1.bandY01, BACKDROP_LAYERS.ISLAND_1.sizeScale, undefined,
    { sideOffsetX: BACKDROP_LAYERS.ISLAND_1.sideOffsetX,
      haze: BACKDROP_LAYERS.ISLAND_1.haze, accent: BACKDROP_LAYERS.ISLAND_1.accent });
  island1.mesh.renderOrder = -17;
  group.add(island1.mesh);

  const island2 = buildBackdropPlate(
    'island2', BACKDROP_LAYERS.ISLAND_2.url, BACKDROP_LAYERS.ISLAND_2.distance,
    BACKDROP_LAYERS.ISLAND_2.parallaxFactor, BACKDROP_LAYERS.ISLAND_2.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    BACKDROP_LAYERS.ISLAND_2.bandY01, BACKDROP_LAYERS.ISLAND_2.sizeScale, undefined,
    { sideOffsetX: BACKDROP_LAYERS.ISLAND_2.sideOffsetX,
      haze: BACKDROP_LAYERS.ISLAND_2.haze, accent: BACKDROP_LAYERS.ISLAND_2.accent });
  island2.mesh.renderOrder = -19.5;
  group.add(island2.mesh);

  const island3 = buildBackdropPlate(
    'island3', BACKDROP_LAYERS.ISLAND_3.url, BACKDROP_LAYERS.ISLAND_3.distance,
    BACKDROP_LAYERS.ISLAND_3.parallaxFactor, BACKDROP_LAYERS.ISLAND_3.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    BACKDROP_LAYERS.ISLAND_3.bandY01, BACKDROP_LAYERS.ISLAND_3.sizeScale, undefined,
    { sideOffsetX: BACKDROP_LAYERS.ISLAND_3.sideOffsetX,
      haze: BACKDROP_LAYERS.ISLAND_3.haze, accent: BACKDROP_LAYERS.ISLAND_3.accent });
  island3.mesh.renderOrder = -17.5;
  group.add(island3.mesh);

  const island4 = buildBackdropPlate(
    'island4', BACKDROP_LAYERS.ISLAND_4.url, BACKDROP_LAYERS.ISLAND_4.distance,
    BACKDROP_LAYERS.ISLAND_4.parallaxFactor, BACKDROP_LAYERS.ISLAND_4.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    BACKDROP_LAYERS.ISLAND_4.bandY01, BACKDROP_LAYERS.ISLAND_4.sizeScale, undefined,
    { sideOffsetX: BACKDROP_LAYERS.ISLAND_4.sideOffsetX,
      haze: BACKDROP_LAYERS.ISLAND_4.haze, accent: BACKDROP_LAYERS.ISLAND_4.accent });
  island4.mesh.renderOrder = -19.8;
  group.add(island4.mesh);

  const island5 = buildBackdropPlate(
    'island5', BACKDROP_LAYERS.ISLAND_5.url, BACKDROP_LAYERS.ISLAND_5.distance,
    BACKDROP_LAYERS.ISLAND_5.parallaxFactor, BACKDROP_LAYERS.ISLAND_5.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    BACKDROP_LAYERS.ISLAND_5.bandY01, BACKDROP_LAYERS.ISLAND_5.sizeScale, undefined,
    { sideOffsetX: BACKDROP_LAYERS.ISLAND_5.sideOffsetX,
      haze: BACKDROP_LAYERS.ISLAND_5.haze, accent: BACKDROP_LAYERS.ISLAND_5.accent });
  island5.mesh.renderOrder = -16.5;
  group.add(island5.mesh);

  const island6 = buildBackdropPlate(
    'island6', BACKDROP_LAYERS.ISLAND_6.url, BACKDROP_LAYERS.ISLAND_6.distance,
    BACKDROP_LAYERS.ISLAND_6.parallaxFactor, BACKDROP_LAYERS.ISLAND_6.nativeAspect,
    camHeight, camBack, camLookAtForward, camFov,
    BACKDROP_LAYERS.ISLAND_6.bandY01, BACKDROP_LAYERS.ISLAND_6.sizeScale, undefined,
    { sideOffsetX: BACKDROP_LAYERS.ISLAND_6.sideOffsetX,
      haze: BACKDROP_LAYERS.ISLAND_6.haze, accent: BACKDROP_LAYERS.ISLAND_6.accent });
  island6.mesh.renderOrder = -19.6;
  group.add(island6.mesh);

  const backdropLayers = [
    skyPlate, cloudsPlate, underCloudPlate,
    island1, island2, island3, island4, island5, island6,
  ];
  // Scratch vectors for `update()`'s per-frame `positionBackdropPlate`
  // calls — reused across the whole loop, every frame, not reallocated
  // per layer (six `new Vector3()` a frame is trivial either way, but
  // this is the same allocation-avoidance discipline the rest of the
  // render hot path already follows).
  const camPosV = new Vector3();
  const lookAtV = new Vector3();

  // ARCHIPELAGO MESHES AND GULLS DELETED (founder, 2026-08-19, item 1);
  // ISLANDS REBUILT AS PLATES (item 4, same date, later in the same
  // session — see the `island1`/`island2`/`island3`/`island4` block
  // just above).
  // What follows is why the ORIGINAL 3D meshes were deleted, not an
  // argument against islands existing at all: "the
  // dark rocks on the LEFT are geometry in front of that seam... delete
  // every one that is decorative rather than cloud, deck, kerb, squad or
  // horde." Enumerated the full scene-graph before deleting anything —
  // six objects rendered outside the deck edges at mid-height, both
  // sides: three floating-isle meshes (`islet1` x=-24 z=76,
  // `islet2` x=26 z=60, `route-B B` x=-16 z=105 — all
  // `IcosahedronGeometry`/`MeshBasicMaterial`, vertex-coloured) and three
  // gull meshes (chevron `BufferGeometry`/`MeshBasicMaterial`, x range
  // roughly +/-20, z 60-120). None of the six is cloud, deck, kerb, squad
  // or horde — all six deleted, along with the now-fully-unused
  // functions that only existed to build them (`makeIsle`, `isleAt`,
  // `hazeFor`, `gullGeometry`, the `Bob`/`Gull` interfaces) rather than
  // leaving that code in place unreachable. `gulls` stays on the returned
  // interface as an always-empty array — `window.__scene.gullInfo()`/
  // `gullScreens()` (scene3d.ts) read it and shouldn't have to change
  // shape for this.
  const gulls: readonly Mesh[] = [];

  function update(
    dt: number, camX: number, camY: number, camZ: number,
    lookAtX: number, lookAtY: number, lookAtZ: number,
  ): void {
    void dt;
    // FULL LIVE TRACKING, THIRD AND FINAL ROUND ON THIS BUG (founder,
    // 2026-08-19, "the environment cuts off when the camera rises") — see
    // `positionBackdropPlate`'s own doc comment for the two prior fixes
    // this supersedes (a coverage-floor margin, then orientation-only
    // re-facing) and why each was necessary but not sufficient. Every
    // plate's full position AND orientation are now re-derived every
    // frame from the LIVE camera position and look-at target, at the
    // plate's own fixed `distance` — the same construction-time math,
    // just re-run continuously instead of once. `camPosV`/`lookAtV` are
    // scratch vectors reused across the loop, not reallocated per layer
    // per frame.
    camPosV.set(camX, camY, camZ);
    lookAtV.set(lookAtX, lookAtY, lookAtZ);
    for (const layer of backdropLayers) {
      positionBackdropPlate(layer, camPosV, lookAtV, camFov);
    }
  }

  function resize(aspect: number, fovDeg: number): void {
    for (const layer of backdropLayers) sizeBackdropPlate(layer, fovDeg, aspect);
  }

  return {
    group, palette: pal, update, resize,
    backdrop: backdropLayers.map(({ name, mesh, texture, parallaxFactor, nativeAspect, bandY01 }) =>
      ({ name, mesh, texture, parallaxFactor, nativeAspect, ...(bandY01 !== undefined ? { bandY01 } : {}) })),
    gulls,
  };
}
